import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../../utils/classnames';

const PercentageInput = ({
  disabled = false,
  label,
  name,
  placeholder = '0,00%',
  required = false,
  value,
  onChange,
  onFocus,
  labelClassName = '',
  inputClassName = '',
  py = 'py-3',
}) => {
  const [localValue, setLocalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const justFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocused) {
      if (value !== null && value !== undefined && value !== '') {
        const formatted = String(value).replace('.', ',');
        setLocalValue(`${formatted}%`);
      } else {
        setLocalValue('');
      }
    }
  }, [value, isFocused]);

  // Seleciona o valor inteiro depois que o `%` já saiu do texto exibido (no render seguinte
  // ao focus) — selecionar durante o próprio handleFocus pegaria o valor antigo, com `%`.
  useEffect(() => {
    if (isFocused) {
      inputRef.current?.select();
    }
  }, [isFocused]);

  const handleFocus = (e) => {
    setIsFocused(true);
    justFocusedRef.current = true;

    if (value !== null && value !== undefined && value !== '') {
      setLocalValue(String(value).replace('.', ','));
    } else {
      setLocalValue('');
    }

    onFocus?.(e);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // O mouseup nativo reposiciona o cursor no ponto clicado, desfazendo o select() do focus —
  // bloqueado só no clique que trouxe o foco; um segundo clique já focado reposiciona normal.
  const handleMouseUp = (e) => {
    if (justFocusedRef.current) {
      e.preventDefault();
      justFocusedRef.current = false;
    }
  };

  const handleChange = (e) => {
    let raw = e.target.value;

    raw = raw.replace(/[^0-9,]/g, '');
    const parts = raw.split(',');
    if (parts.length > 2) return;

    const numericTest = parseFloat(raw.replace(',', '.'));
    if (numericTest > 100) {
      raw = '100';
    }

    if (parts[1] && parts[1].length > 2) {
      raw = `${parts[0]},${parts[1].substring(0, 2)}`;
    }

    setLocalValue(raw);

    if (raw === '' || raw === ',') {
      onChange({ target: { name, value: null } });
    } else {
      const numericVal = parseFloat(raw.replace(',', '.'));
      onChange({ target: { name, value: numericVal } });
    }
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={name} className={cn('text-blueDarker font-normal', labelClassName)}>
          {label}
        </label>
      )}
      <input
        ref={inputRef}
        id={name}
        name={name}
        type="text"
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseUp={handleMouseUp}
        autoComplete="off"
        className={cn(
          py,
          'w-full border border-bluePale rounded-xl px-3 placeholder-blueSteel',
          'transition-all duration-200 focus:outline-none focus:border-tealMedium focus:ring-1 focus:ring-tealMedium',
          inputClassName
        )}
      />
    </div>
  );
};

export default PercentageInput;
